import { ErrorTracking } from '@shared/tracking';
import { ContextTypeApi } from 'api-typings/messages/messages_types';

import { BidAwardStatusApi } from 'api-typings/projects/projects';
import { BidsUpdateActionRawPayload } from '../bids/bids.backend-model';
import { SearchCollections } from '../collections.model';
import {
  MessagesPostApiResponse,
  MessagesPostRawPayload,
} from '../messages/messages.backend-model';
import {
  NotificationsPreferencePostRawPayload,
  NotificationsPreferenceResultAjax,
} from '../notifications-preferences/notifications-preferences.backend-model';
import { SearchHistoryPostRawPayload } from '../search-history/search-history.backend-model';
import {
  ThreadsPostApiResponse,
  ThreadsPostRawPayload,
  ThreadsUpdateActionRawPayload,
} from '../threads/threads.backend-model';
import { Thread } from '../threads/threads.model';

import {
  MilestonesPostApiResponse,
  MilestonesPostRawPayload,
} from '../milestones/milestones.backend-model';

import {
  DeleteRequestPayload,
  PushRequestPayload,
  SetRequestPayload,
  UpdateRequestPayload,
} from './actions';
import { toNumber } from './helpers';
import { RootModel } from './root.model';
import { pluckItemFromRawStore } from './store.helpers';
import { RawQuery, Reference, SearchQuery, StoreState } from './store.model';

export interface Params {
  [key: string]:
    | string
    | number
    | ReadonlyArray<number | undefined>
    | ReadonlyArray<string | undefined>
    | undefined;
}

export interface BackendConfig {
  endpoint: string;
  isGaf?: boolean;
  params: Params;
}

type FetchRequest<K extends keyof RootModel> = ((
  key1: string,
  key2: string[] | undefined,
  query?: RawQuery<RootModel[K]>,
) => BackendConfig);

type PostRequest<K extends keyof RootModel> = ((
  key: string,
  entity: RootModel[K],
) => BackendConfig);

export interface Backend<K extends keyof RootModel> {
  fetch?: FetchRequest<K>;
  post?: PostRequest<K>;
}

export interface BackendSearch {
  fetch: ((query: SearchQuery) => BackendConfig);
}

export const backend: { [K in keyof RootModel]?: Backend<K> } = {};

export const backendSearch: {
  [K in keyof SearchCollections]?: BackendSearch
} = {};

export type BackendFetchTypes = BackendAjaxFetchTypes & BackendApiFetchTypes;

export type BackendPushPayloadTypes = {
  messages: MessagesPostRawPayload;
  searchHistory: any; // TODO
  threads: ThreadsPostRawPayload;
  milestones: MilestonesPostRawPayload;
} & { [k: string]: never };

export type BackendPushReturnTypes = {
  messages: MessagesPostApiResponse;
  searchHistory: any; // TODO
  threads: ThreadsPostApiResponse;
  milestones: MilestonesPostApiResponse;
} & { [k: string]: never };

export type BackendSetPayloadTypes = {
  notificationsPreferences: NotificationsPreferencePostRawPayload;
} & { [k: string]: never };

export type BackendSetReturnTypes = {
  notificationsPreferences: NotificationsPreferenceResultAjax;
} & { [k: string]: never };

export type BackendUpdatePayloadTypes = {
  threads: ThreadsUpdateActionRawPayload;
  bids: BidsUpdateActionRawPayload;
} & { [k: string]: never };

export type BackendUpdateReturnTypes = {} & { [k: string]: never };

export type BackendDeletePayloadTypes = {} & { [k: string]: never };

export type BackendDeleteReturnTypes = {} & { [k: string]: never };

export function getPushPayload<K extends keyof RootModel>(
  reference: Reference<K>,
  object: RootModel[K],
  errorTracking: ErrorTracking,
  extra?: { [index: string]: string | number },
): PushRequestPayload<K> | undefined {
  const type = reference.path.collection;
  switch (type) {
    case 'threads': {
      const ref = reference;
      const thread = object as RootModel['threads'];
      const rawRequest: ThreadsPostRawPayload = {
        context:
          thread.context.type !== ContextTypeApi.NONE
            ? thread.context.id
            : undefined,
        context_type: thread.context.type,
        thread_type: thread.threadType,
        members: thread.members,
        message: (thread.message && thread.message.message) || '',
        source: 21,
      };
      return { type, ref, object: thread, rawRequest, asJSON: true };
    }
    case 'milestones': {
      const ref = reference;
      const milestone = object as RootModel['milestones'];
      if (!milestone.description) {
        throw new Error('Milestone description not provided on push');
      }
      const rawRequest: MilestonesPostRawPayload = {
        bidder_id: milestone.bidderId,
        amount: milestone.amount,
        project_id: milestone.projectId,
        // TODO - reason: milestone.reason,
        description: milestone.description,
        request_id: extra ? toNumber(extra.requestId) : undefined,
      };
      return { type, ref, object: milestone, rawRequest, asJSON: true };
    }
    case 'messages': {
      const ref = reference;
      const message = object as RootModel['messages'];
      const rawRequest: MessagesPostRawPayload = {
        message: message.message,
        client_message_id: message.clientMessageId
          ? String(message.clientMessageId)
          : String(Date.now()),
        source: message.messageSource,
      };
      return { type, ref, object: message, rawRequest, asJSON: false };
    }
    case 'searchHistory': {
      const ref = reference;
      const data = object as RootModel['searchHistory'];
      const rawRequest: SearchHistoryPostRawPayload = {
        keyword: data.keyword,
      };
      return { type, ref, object: data, rawRequest, asJSON: true };
    }

    default:
      errorTracking.captureMessage(`Cannot push for ${type}.`, {
        extra: { reference, object },
      });
      return undefined;
  }
}

export function getSetPayload<K extends keyof RootModel>(
  reference: Reference<K>,
  id: string | number,
  object: RootModel[K],
  store: StoreState,
  errorTracking: ErrorTracking,
): SetRequestPayload<K> | undefined {
  const type = reference.path.collection;

  switch (type) {
    case 'notificationsPreferences': {
      const ref = reference;
      const originalObject = pluckItemFromRawStore(store, ref.path, id);
      if (originalObject === undefined) {
        return undefined;
      }
      const notificationPreference = object as RootModel['notificationsPreferences'];
      const rawRequest: NotificationsPreferencePostRawPayload = {
        settings: JSON.stringify([notificationPreference]),
      };
      return {
        type,
        ref,
        object: notificationPreference,
        rawRequest,
        originalObject,
      };
    }

    default:
      errorTracking.captureMessage(`Cannot push for ${type}.`, {
        extra: { reference, object },
      });
      return undefined;
  }
}

export function getUpdatePayload<K extends keyof RootModel>(
  reference: Reference<K>,
  id: string | number,
  object: Partial<RootModel[K]>,
  store: StoreState,
  errorTracking: ErrorTracking,
):
  | {
      payload: UpdateRequestPayload<K>;
      endpoint: string;
      method: 'POST' | 'PUT';
    }
  | undefined {
  const type = reference.path.collection;
  const captureMessageError = { extra: { reference, object } };
  let method: 'PUT' | 'POST' = 'PUT';

  switch (type) {
    case 'threads': {
      const ref = reference;
      const originalObject = pluckItemFromRawStore(store, ref.path, id);
      if (originalObject === undefined) {
        return undefined;
      }
      const originalThread = originalObject as Thread;
      const thread = (object as any) as Partial<RootModel['threads']>;
      let rawRequest: ThreadsUpdateActionRawPayload | undefined;
      let endpoint: string | undefined;

      if (thread.isRead === true) {
        if (rawRequest) {
          errorTracking.captureMessage(
            `Cannot update two fields at once for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }
        rawRequest = {
          threads: [toNumber(id)],
          action: 'read',
        };
        endpoint = `messages/0.1/threads/`;
      }

      if (thread.isMuted !== undefined) {
        if (rawRequest) {
          errorTracking.captureMessage(
            `Cannot update two fields at once for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }
        rawRequest = {
          threads: [toNumber(id)],
          action: thread.isMuted ? 'mute' : 'unmute',
        };
        endpoint = `messages/0.1/threads/`;
      }

      if (thread.isBlocked !== undefined) {
        if (rawRequest) {
          errorTracking.captureMessage(
            `Cannot update two fields at once for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }
        rawRequest = {
          threads: [toNumber(id)],
          action: thread.isBlocked ? 'block' : 'unblock',
        };
        endpoint = `messages/0.1/threads/`;
      }

      if (thread.folder) {
        if (rawRequest) {
          errorTracking.captureMessage(
            `Cannot update two fields at once for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }

        if (thread.folder === 'inbox') {
          rawRequest = { threads: [toNumber(id)], action: 'unarchive' };
        } else if (thread.folder === 'archived') {
          rawRequest = { threads: [toNumber(id)], action: 'archive' };
        } else {
          errorTracking.captureMessage(
            `Cannot push for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }
        endpoint = `messages/0.1/threads/`;
      }

      if (
        thread.members &&
        thread.members.length !== originalThread.members.length
      ) {
        if (rawRequest) {
          errorTracking.captureMessage(
            `Cannot update two fields at once for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }
        endpoint = `messages/0.1/threads/${originalThread.id}/members/`;
        if (thread.members.length > originalThread.members.length) {
          rawRequest = {
            thread_id: originalThread.id,
            action: 'add',
            members: thread.members.filter(
              m => !originalThread.members.includes(m),
            ),
          };
        } else if (thread.members.length < originalThread.members.length) {
          rawRequest = {
            thread_id: originalThread.id,
            action: 'remove',
            members: originalThread.members.filter(
              m => !(thread.members || []).includes(m),
            ),
          };
        }
      }

      if (thread.typing) {
        if (rawRequest) {
          errorTracking.captureMessage(
            `Cannot update two fields at once for ${type}.`,
            captureMessageError,
          );
          return undefined;
        }
        rawRequest = { thread_id: originalThread.id };
        endpoint = `messages/0.1/threads/${originalThread.id}/typing/`;
        method = 'POST';
      }
      if (rawRequest === undefined || endpoint === undefined) {
        errorTracking.captureMessage(
          `Cound not update any fields of ${type}.`,
          captureMessageError,
        );
        return undefined;
      }

      return {
        payload: {
          type,
          ref,
          object: thread as any,
          rawRequest,
          originalObject,
        },
        endpoint,
        method,
      };
    }
    case 'bids': {
      const ref = reference;
      const originalObject = pluckItemFromRawStore(store, ref.path, id);
      if (originalObject === undefined) {
        return undefined;
      }
      const bid = (object as any) as Partial<RootModel['bids']>;
      if (
        bid.awardStatus === BidAwardStatusApi.PENDING ||
        bid.awardStatus === BidAwardStatusApi.AWARDED
      ) {
        const endpoint = `projects/0.1/bids/${id}`;
        const rawRequest: BidsUpdateActionRawPayload = {
          bid_id: toNumber(id),
          action:
            bid.awardStatus === BidAwardStatusApi.PENDING ? 'award' : 'accept',
        };
        return {
          payload: {
            type,
            ref,
            object: bid as any,
            rawRequest,
            originalObject,
          },
          endpoint,
          method,
        };
      }
      return undefined;
    }
    default:
      errorTracking.captureMessage(
        `Cannot update for ${type}.`,
        captureMessageError,
      );
      return undefined;
  }
}

export function getDeletePayload<K extends keyof RootModel>(
  reference: Reference<K>,
  id: string | number,
  store: StoreState,
  errorTracking: ErrorTracking,
):
  | {
      payload: DeleteRequestPayload<K>;
      endpoint: string;
      method: 'POST' | 'PUT' | 'DELETE';
    }
  | undefined {
  const type = reference.path.collection;
  const captureMessageError = { extra: { reference } };

  switch (type) {
    default:
      errorTracking.captureMessage(
        `Cannot delete for ${type}.`,
        captureMessageError,
      );
      return undefined;
  }
}



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/core/backend.ts